import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  ScrollView,
  ActivityIndicator,
  Modal,
  RefreshControl,
} from 'react-native';
import { useMediaQuery } from 'react-responsive';
import { API_URL } from '../config';
import { styles as mobileStyles } from '../styles/pages/sslMonitoringStyles';
import { styles as desktopStyles } from '../styles/pages/sslMonitoringDesktopStyles';

interface SSLDomain {
  id: number;
  domain: string;
  issuer: string;
  validFrom: Date;
  validTo: Date;
  daysRemaining: number;
  status: 'valid' | 'expiring' | 'expired';
  lastChecked: Date;
}

const SSLMonitoring: React.FC = () => {
  const [domains, setDomains] = useState<SSLDomain[]>([]);
  const [newDomain, setNewDomain] = useState('');
  const [modalVisible, setModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingDomain, setEditingDomain] = useState<SSLDomain | null>(null);
  const [editDomainValue, setEditDomainValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const isDesktop = useMediaQuery({ minWidth: 768 });
  const styles = isDesktop ? { ...mobileStyles, ...desktopStyles } : mobileStyles;

  useEffect(() => {
    fetchDomains();
    
    // 다음 정각까지 남은 시간 계산
    const now = new Date();
    const nextHour = new Date(now);
    nextHour.setHours(now.getHours() + 1, 0, 0, 0);
    const timeUntilNextHour = nextHour.getTime() - now.getTime();
    
    // 첫 번째 갱신을 다음 정각에 실행
    const timeout = setTimeout(() => {
      fetchDomains();
      
      // 이후 매 정각마다 갱신
      const interval = setInterval(fetchDomains, 3600000); // 1시간마다 갱신
      
      // cleanup 함수에서 interval 정리
      return () => clearInterval(interval);
    }, timeUntilNextHour);
    
    return () => clearTimeout(timeout);
  }, []);

  const fetchDomains = async () => {
    try {
      const response = await fetch(`${API_URL}/api/ssl-monitoring/domains`);
      const result = await response.json();
      // ResponseInterceptor로 감싸진 응답에서 data 추출
      setDomains(result.data || []);
    } catch (error) {
      console.error('Failed to fetch domains:', error);
    }
  };

  const onRefresh = async () => {
    setRefreshing(true);
    await fetchDomains();
    setRefreshing(false);
  };

  const addDomain = async () => {
    if (!newDomain) {
      alert('도메인을 입력해주세요.');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/ssl-monitoring/domains`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ domain: newDomain }),
      });

      const result = await response.json();
      if (response.ok && result.success) {
        alert('도메인이 추가되었습니다.');
        setNewDomain('');
        setModalVisible(false);
        fetchDomains();
      } else {
        alert(result.error || '도메인 추가에 실패했습니다.');
      }
    } catch (error) {
      alert('네트워크 오류가 발생했습니다.');
      console.error('Add domain error:', error);
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = (domain: SSLDomain) => {
    setEditingDomain(domain);
    setEditDomainValue(domain.domain);
    setEditModalVisible(true);
  };

  const updateDomain = async () => {
    if (!editDomainValue || !editingDomain) {
      alert('도메인을 입력해주세요.');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/ssl-monitoring/domains/${editingDomain.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ domain: editDomainValue }),
      });

      const result = await response.json();
      if (response.ok && result.success) {
        alert('도메인이 수정되었습니다.');
        setEditModalVisible(false);
        setEditingDomain(null);
        setEditDomainValue('');
        fetchDomains();
      } else {
        alert(result.error || '도메인 수정에 실패했습니다.');
      }
    } catch (error) {
      alert('네트워크 오류가 발생했습니다.');
      console.error('Update domain error:', error);
    } finally {
      setLoading(false);
    }
  };

  const deleteDomain = async (id: number) => {
    // 웹 환경에서 동작하는 확인 다이얼로그
    if (window.confirm('이 도메인을 삭제하시겠습니까?')) {
      try {
        const response = await fetch(`${API_URL}/api/ssl-monitoring/domains/${id}`, {
          method: 'DELETE',
        });

        const result = await response.json();

        if (response.ok && result.success) {
          alert('도메인이 삭제되었습니다.');
          fetchDomains();
        } else {
          alert(result.error || '도메인 삭제에 실패했습니다.');
        }
      } catch (error) {
        alert('네트워크 오류가 발생했습니다.');
        console.error('Delete error:', error);
      }
    }
  };

  const refreshDomain = async (id: number) => {
    try {
      const response = await fetch(`${API_URL}/api/ssl-monitoring/domains/${id}/refresh`, {
        method: 'POST',
      });

      const result = await response.json();

      if (response.ok && result.success) {
        alert('인증서 정보가 갱신되었습니다.');
        fetchDomains();
      } else {
        alert(result.error || '새로고침에 실패했습니다.');
      }
    } catch (error) {
      alert('네트워크 오류가 발생했습니다.');
      console.error('Refresh error:', error);
    }
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'valid':
        return '#4CAF50';
      case 'expiring':
        return '#FF9800';
      case 'expired':
        return '#F44336';
      default:
        return '#757575';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'valid':
        return '정상';
      case 'expiring':
        return '만료 임박';
      case 'expired':
        return '만료됨';
      default:
        return '알 수 없음';
    }
  };

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.title}></Text>
        <TouchableOpacity
          style={styles.addButton}
          onPress={() => setModalVisible(true)}
        >
          <Text style={styles.addButtonText}>+ 도메인 추가</Text>
        </TouchableOpacity>
      </View>

      <ScrollView
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
      >
        {domains.length === 0 ? (
          <View style={styles.emptyState}>
            <Text style={styles.emptyText}>등록된 도메인이 없습니다.</Text>
            <Text style={styles.emptyText}>도메인을 추가해주세요.</Text>
          </View>
        ) : (
          <View style={styles.gridContainer}>
            {domains.map((domain) => (
              <View key={domain.id} style={styles.domainCardWrapper}>
                <View style={styles.domainCard}>
                  <View style={styles.domainHeader}>
                    <Text style={styles.domainName} numberOfLines={1} ellipsizeMode="tail">
                      {domain.domain}
                    </Text>
                    <View
                      style={[
                        styles.statusBadge,
                        { backgroundColor: getStatusColor(domain.status) },
                      ]}
                    >
                      <Text style={styles.statusText}>
                        {getStatusText(domain.status)}
                      </Text>
                    </View>
                  </View>

                  <View style={styles.infoRow}>
                    <Text style={styles.infoLabel}>발급자:</Text>
                    <Text style={styles.infoValue}>{domain.issuer || '-'}</Text>
                  </View>

                  <View style={styles.infoRow}>
                    <Text style={styles.infoLabel}>발급일:</Text>
                    <Text style={styles.infoValue}>
                      {domain.validFrom ? formatDate(domain.validFrom) : '-'}
                    </Text>
                  </View>

                  <View style={styles.infoRow}>
                    <Text style={styles.infoLabel}>만료일:</Text>
                    <Text style={styles.infoValue}>
                      {domain.validTo ? formatDate(domain.validTo) : '-'}
                    </Text>
                  </View>

                  <View style={styles.infoRow}>
                    <Text style={styles.infoLabel}>남은 일수:</Text>
                    <Text
                      style={[
                        styles.infoValue,
                        domain.daysRemaining <= 7 && styles.daysWarning,
                      ]}
                    >
                      {domain.daysRemaining !== null
                        ? `${domain.daysRemaining}일`
                        : '-'}
                    </Text>
                  </View>

                  <View style={styles.infoRow}>
                    <Text style={styles.infoLabel}>마지막 확인:</Text>
                    <Text style={styles.infoValue}>
                      {domain.lastChecked ? formatDate(domain.lastChecked) : '-'}
                    </Text>
                  </View>

                  <View style={styles.actionButtons}>
                    <TouchableOpacity
                      style={[styles.actionButton, styles.refreshButton]}
                      onPress={() => refreshDomain(domain.id)}
                    >
                      <Text style={styles.actionButtonText}>새로고침</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={[styles.actionButton, styles.editButton]}
                      onPress={() => openEditModal(domain)}
                    >
                      <Text style={styles.actionButtonText}>수정</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={[styles.actionButton, styles.deleteButton]}
                      onPress={() => deleteDomain(domain.id)}
                    >
                      <Text style={styles.actionButtonText}>삭제</Text>
                    </TouchableOpacity>
                  </View>
                </View>
              </View>
            ))}
          </View>
        )}
      </ScrollView>

      <Modal
        animationType="fade"
        transparent={true}
        visible={modalVisible}
        onRequestClose={() => setModalVisible(false)}
      >
        <View style={styles.modal}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>도메인 추가</Text>
            <TextInput
              style={styles.input}
              placeholder="example.com"
              value={newDomain}
              onChangeText={setNewDomain}
              autoCapitalize="none"
              autoCorrect={false}
            />
            <View style={styles.modalButtons}>
              <TouchableOpacity
                style={[styles.modalButton, styles.cancelButton]}
                onPress={() => setModalVisible(false)}
              >
                <Text style={styles.modalButtonText}>취소</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.modalButton, styles.confirmButton]}
                onPress={addDomain}
                disabled={loading}
              >
                {loading ? (
                  <ActivityIndicator color="white" />
                ) : (
                  <Text style={styles.modalButtonText}>추가</Text>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      <Modal
        animationType="fade"
        transparent={true}
        visible={editModalVisible}
        onRequestClose={() => setEditModalVisible(false)}
      >
        <View style={styles.modal}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>도메인 수정</Text>
            <TextInput
              style={styles.input}
              placeholder="example.com"
              value={editDomainValue}
              onChangeText={setEditDomainValue}
              autoCapitalize="none"
              autoCorrect={false}
            />
            <View style={styles.modalButtons}>
              <TouchableOpacity
                style={[styles.modalButton, styles.cancelButton]}
                onPress={() => {
                  setEditModalVisible(false);
                  setEditingDomain(null);
                  setEditDomainValue('');
                }}
              >
                <Text style={styles.modalButtonText}>취소</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.modalButton, styles.confirmButton]}
                onPress={updateDomain}
                disabled={loading}
              >
                {loading ? (
                  <ActivityIndicator color="white" />
                ) : (
                  <Text style={styles.modalButtonText}>수정</Text>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
};

export default SSLMonitoring;